import { PropsWithChildren } from 'react';
import { type DefaultTheme, ThemeProvider } from 'styled-components';

import { useWorkspace } from '@treams/treams-hooks';
import {
	AnnouncementDisplayType,
	WorkspaceTheme,
	useAnnouncementsQuery,
	useViewerWorkspacePlanQuery,
	useWorkspaceThemeQuery,
} from '@treams/treams-network';
import {
	GlobalStyle,
	defaultTheme,
	placeholderTheme,
	replaceColorsAndFontsInTheme,
} from '@treams/treams-ui';

export const WorkspaceThemeProvider = ({ children }: PropsWithChildren) => {
	const workspace = useWorkspace();

	const { data, loading } = useWorkspaceThemeQuery({
		variables: { id: workspace.id },
	});

	const { data: planData } = useViewerWorkspacePlanQuery();

	const isTrialEnvironment =
		planData?.viewer.workspace.plans?.every((plan) => plan.isTrial) ?? false;

	const { data: announcementsData } = useAnnouncementsQuery({
		variables: {
			displayType: AnnouncementDisplayType.Banner,
			first: 10,
		},
		skip: isTrialEnvironment,
	});

	const hasUnreadAnnouncementBanner =
		announcementsData?.announcements?.edges
			?.map(({ node }) => node)
			.some((node) => node.enabled && !node.isRead) ?? false;

	const hasAnnouncementBanner = isTrialEnvironment || hasUnreadAnnouncementBanner;

	const theme = loading
		? (placeholderTheme as unknown as DefaultTheme)
		: data
			? replaceColorsAndFontsInTheme(data.workspace.theme as WorkspaceTheme)
			: defaultTheme;

	return (
		<ThemeProvider theme={{ ...theme, isTrialEnvironment, hasAnnouncementBanner }}>
			{children}
		</ThemeProvider>
	);
};

export const BaseThemeProvider = ({ children }: PropsWithChildren) => {
	return (
		<ThemeProvider theme={defaultTheme}>
			<GlobalStyle />
			{children}
		</ThemeProvider>
	);
};
